/**
 * SEO copy for every page, in one place.
 *
 * Shared by the runtime (src/hooks/useSeo.js) and the build-time prerender
 * (scripts/prerender.js) so the <head> Googlebot receives before executing JS
 * is the same one React renders afterwards.
 *
 * `i18nKey` is where the translated copy lives inside the market's CMS content:
 * `seo.<page>`, edited from the CMS under Frontsite -> SEO. When
 * `<i18nKey>.title` / `.description` / `.keywords` exist, that copy wins; the
 * English strings below are the fallback.
 *
 * The page key uses snake_case inside the CMS because its flatten()/unflatten()
 * uses "-" as the separator and a hyphen would split the key into two levels.
 */

/**
 * Amplify sirve las páginas prerenderizadas desde directorios, así que /tr
 * responde 301 hacia /tr/. La barra final es la forma canónica: sin esto el
 * canonical y el sitemap apuntarían a URLs que redirigen.
 */
export const withTrailingSlash = (path) => (path.endsWith("/") ? path : `${path}/`);

export const PAGE_SEO = {
    "news": {
        i18nKey: "seo.news",
        title: "News & Insights | Rainbow Agro",
        description: "Read the latest Rainbow Agro news: product launches, market expansions, regulatory approvals, and agrochemical industry insights from our global operations.",
        keywords: "Rainbow Agro news, agrochemical news, crop protection updates, product launches, market expansion",
    },
    "news-single": {
        i18nKey: "seo.news_single",
        title: "News | Rainbow Agro",
        description: "Read the latest news and agronomic insights from Rainbow Agro, a global crop protection company operating in 100+ countries.",
        keywords: "Rainbow Agro news, crop protection, agronomy",
    },
    "product-single": {
        i18nKey: "seo.product_single",
        title: "Crop Protection Product | Rainbow Agro",
        description: "Product details, registered uses, target crops and documentation for Rainbow Agro crop protection products.",
        keywords: "crop protection product, herbicide, fungicide, insecticide, Rainbow Agro",
    },
    "home": {
        i18nKey: "seo.home",
        title: "Crop Protection & Agricultural Solutions | Rainbow Agro",
        description: "Rainbow Agro is a global top-10 crop protection company providing herbicides, fungicides, insecticides, fertilizers, and biostimulants across 100+ countries.",
        keywords: "crop protection, herbicides, fungicides, insecticides, fertilizers, biostimulants, Rainbow Agro",
    },
    "company": {
        i18nKey: "seo.company",
        title: "About Rainbow Agro | Global Agrochemical Manufacturer",
        description: "Founded in 2000 in Weifang, China, Rainbow Agro is a leading global crop protection company and top Chinese exporter with 9 manufacturing plants and operations in 100+ countries.",
        keywords: "Rainbow Agro company, agrochemical manufacturer, Shandong Weifang, Chinese exporter, crop protection",
    },
    "service-units": {
        i18nKey: "seo.service_units",
        title: "Services & Business Units | Smart Growing, BioSolutions & More | Rainbow Agro",
        description: "Explore Rainbow Agro's service divisions: Smart Growing for precision agriculture, BioSolutions, Enviro Solutions, Design Studio, and Business Partners — each built for specific market needs.",
        keywords: "Rainbow Agro services, smart growing, biosolutions, Enviro Solutions, design studio, business units, agricultural services",
    },
    "contact": {
        i18nKey: "seo.contact",
        title: "Contact Rainbow Agro | Crop Protection Support",
        description: "Contact Rainbow Agro to find herbicides, fungicides, insecticides, and crop protection solutions for your country. Support available for distributors, partners, and growers globally.",
        keywords: "contact Rainbow Agro, crop protection support, agrochemical partner, distributor contact",
    },
    "solutions": {
        i18nKey: "seo.solutions",
        title: "Crop Solutions | Weed, Pest & Disease Control | Rainbow Agro",
        description: "End-to-end crop protection solutions from Rainbow Agro for weed control, pest management, and disease prevention across cereals, vegetables, fruit crops, and specialty crops.",
        keywords: "crop solutions, weed control, pest management, disease control, integrated crop protection, cereals, vegetables",
    },
    "smart-growing": {
        i18nKey: "seo.smart_growing",
        title: "Smart Growing | Precision Agriculture & Digital Agronomy | Rainbow Agro",
        description: "Rainbow Smart Growing combines precision agriculture technology, crop intelligence, and digital agronomy services to optimize yields and reduce input costs for growers worldwide.",
        keywords: "smart growing, precision agriculture, digital agronomy, crop management, yield optimization, agronomy technology",
    },
    "business-partners": {
        i18nKey: "seo.business_partners",
        title: "Business Partners | Join Rainbow Agro's Global Network",
        description: "Become a Rainbow Agro business partner. Access tailored crop protection solutions, technical support, and supply chain services designed for B2B agricultural distributors.",
        keywords: "Rainbow Agro business partners, agrochemical distributor, B2B agriculture partnership, crop protection distribution",
    },
    "bio-solutions": {
        i18nKey: "seo.bio_solutions",
        title: "BioSolutions | Biostimulants & Biopesticides | Rainbow Agro",
        description: "Rainbow BioSolutions offers biostimulants, biopesticides, and biocontrol products for sustainable, residue-free crop protection. Improve yields naturally with science-backed biological solutions.",
        keywords: "biosolutions, biostimulants, biopesticides, biocontrol, biological crop protection, sustainable farming, residue-free",
    },
    "enviro-solutions": {
        i18nKey: "seo.enviro_solutions",
        title: "Enviro Solutions | Turf, Forestry & Industrial Herbicides | Rainbow Agro",
        description: "Rainbow Enviro provides herbicide and pest control solutions for turf management, forestry, industrial vegetation control, and public health — beyond agricultural fields.",
        keywords: "Enviro solutions, turf herbicides, industrial weed control, forestry pest management, vegetation control, public health",
    },
    "design": {
        i18nKey: "seo.design",
        title: "Design Studio | Custom Agrochemical Formulations | Rainbow Agro",
        description: "Rainbow Design Studio co-develops tailor-made agrochemical formulations and supply chain solutions for distributors, retailers, and private-label partners worldwide.",
        keywords: "agrochemical formulation, private label crop protection, custom formulation, supply chain, design studio Rainbow",
    },
    "privacy": {
        i18nKey: "seo.privacy",
        title: "Privacy Statement | Rainbow Agro",
        description: "Learn about Rainbow Agro's privacy practices, including data collection, use of personal information, and your rights as a user of our site.",
        keywords: "privacy statement, Rainbow Agro, data protection, cookies policy, personal information, user privacy",
    },
    "terms": {
        i18nKey: "seo.terms",
        title: "Terms & Conditions | Rainbow Agro",
        description: "Explore the terms and conditions governing the use of Rainbow Agro's website, including legal disclaimers and global availability policies.",
        keywords: "terms and conditions, Rainbow Agro, website policies, legal terms, global availability",
    },
    "product-list": {
        i18nKey: "seo.product_list",
        title: "Crop Protection Products | Herbicides, Fungicides & More | Rainbow Agro",
        description: "Browse Rainbow Agro's product portfolio registered in your country — herbicides, fungicides, insecticides, fertilizers, plant growth regulators, and biostimulants.",
        keywords: "crop protection products, herbicides, fungicides, insecticides, fertilizers, biostimulants, plant growth regulators",
    },
    "product-global-list": {
        i18nKey: "seo.product_global_list",
        title: "Global Product Portfolio | Rainbow Agro",
        description: "Explore Rainbow Agro's complete global agrochemical portfolio — herbicides, fungicides, insecticides, fertilizers, biostimulants, and specialty formulations.",
        keywords: "global crop protection, agrochemical portfolio, herbicides, fungicides, insecticides, Rainbow Agro",
    },
    "info": {
        i18nKey: "seo.info",
        title: "Company Information | Rainbow Agro",
        description: "Stay updated with the latest news, insights, and achievements from Rainbow’s global team.",
        keywords: "news, agriculture, Rainbow updates",
    },
    "investors": {
        i18nKey: "seo.investors",
        title: "Investor Relations | Rainbow Agro – Top 10 Crop Protection Company",
        description: "Rainbow Agro investor hub: Leading Global Crop Protection Company listed on the Shenzhen Stock Exchange (301035). Discover financial performance, growth strategy, and global expansion data.",
        keywords: "Rainbow Agro investors, 301035, Shenzhen Stock Exchange, agrochemical investment, crop protection stock",
    },
    "custom-solutions": {
        i18nKey: "seo.custom_solutions",
        title: "Custom Solutions | Tailored Crop Protection | Rainbow Agro",
        description: "Explore Smart Growing, Business Partners, Bio Solutions...",
        keywords: "smart growing, bio solutions, Enviro, design studio",
    },
    "local-report": {
        i18nKey: "seo.local_report",
        title: "Local Market Reports | Rainbow Agro",
        description: "Country-specific crop protection reports from Rainbow Agro — local product performance, agrochemical market data, and regulatory insights for your region.",
        keywords: "local agrochemical report, country crop protection, Rainbow Agro local market, regional agro data",
    },
    "reports": {
        i18nKey: "seo.reports",
        title: "Market Reports | Agrochemical Insights | Rainbow Agro",
        description: "Access Rainbow Agro market reports and agrochemical industry insights covering crop protection trends, regional performance, and product registration data.",
        keywords: "agrochemical market reports, crop protection insights, Rainbow Agro analysis, agricultural trends",
    },
    "product-b2b-list": {
        i18nKey: "seo.product_b2b_list",
        title: "B2B Product Catalogue | Agrochemical Sourcing | Rainbow Agro",
        description: "Rainbow Agro's B2B product catalogue for distributors and commercial partners: formulations, technical grades, generic actives, and co-formulation options available globally.",
        keywords: "B2B agrochemical products, distributor catalogue, technical grade, formulation supply, Rainbow Agro B2B",
    },
    "product-b2b-contact": {
        i18nKey: "seo.product_b2b_contact",
        title: "B2B Contact | Source Agrochemicals From Rainbow Agro",
        description: "Contact Rainbow Agro's commercial team to source herbicides, fungicides, insecticides, and specialty crop protection products for distribution or private-label business worldwide.",
        keywords: "B2B contact, agrochemical supplier, crop protection sourcing, private label, Rainbow Agro partnership",
    },
    "not-found": {
        i18nKey: "seo.not_found",
        title: "Not Found - Rainbow Agro",
        description: "The page you were looking for could not be found.",
        keywords: "404, not found, Rainbow Agro",
    },
};

/** Route path (without the /:country prefix) -> key in PAGE_SEO. */
export const ROUTE_SEO = {
    "": "home",
    "/news": "news",
    "/company": "company",
    "/serviceunits": "service-units",
    "/contact-us": "contact",
    "/solutions": "solutions",
    "/smart-growing": "smart-growing",
    "/business-partners": "business-partners",
    "/bio-solutions": "bio-solutions",
    "/enviro-solutions": "enviro-solutions",
    "/design": "design",
    "/privacy-statement": "privacy",
    "/terms-and-conditions": "terms",
    "/product-list": "product-list",
    "/product-global-list": "product-global-list",
    "/info": "info",
    "/investors": "investors",
    "/custom-solutions": "custom-solutions",
    "/local-report": "local-report",
};


/**
 * Country types with a navigation that differs from the standard site.
 * Used as the default when the live countries.json is not available (the
 * runtime has no need to fetch it just to build hreflang).
 */
export const KNOWN_COUNTRY_TYPES = {
    ar: "internal_website_b2b",
    in: "internal_website_b2b",
    vn: "internal_website_b2b",
    ca: "internal_website_custom_solutions",
};


/**
 * Rutas cuyo contenido es propio de cada mercado: el slug de un producto o el id
 * de una noticia no tienen equivalente en los demás países, así que declarar
 * alternates mandaría a Googlebot a URLs que no existen e invalidaría el grupo.
 * Estas páginas llevan canonical, pero no cluster de hreflang.
 */
const MARKET_SPECIFIC_ROUTES = [
    /^\/product(\/|$)/,
    /^\/news\//,
    /^\/reports(\/|$)/,
    /^\/product-b2b-list(\/|$)/,
    /^\/product-b2b-contact(\/|$)/,
];

export const routeHasAlternates = (routePath) =>
    !MARKET_SPECIFIC_ROUTES.some((re) => re.test(routePath));

/**
 * Whether a route actually exists in a market, so hreflang, the sitemap and the
 * prerender never point at a URL the navigation cannot reach.
 *
 *   internal_website_b2b (AR, IN, VN)      -> solutions lives at /product-b2b-list/:id
 *   internal_website_custom_solutions (CA) -> uses /custom-solutions
 *   US                                     -> nav links to /custom-solutions
 *   /investors                             -> global hub only
 */
export const routeExistsInMarket = (routePath, market, countryTypes = KNOWN_COUNTRY_TYPES) => {
    const type = countryTypes[market] || "internal_website";
    if (routePath === "/investors") return market === "gl";
    if (routePath === "/solutions") {
        if (type === "internal_website_b2b") return false;
        if (type === "internal_website_custom_solutions") return false;
        if (market === "us") return false;
    }
    return true;
};

/**
 * /solutions renders a different page depending on the market: the global hub
 * gets the editorial Solutions page, every country gets its product list.
 */
export const seoKeyForRoute = (routePath, market) => {
    if (routePath === "/solutions" && market !== "gl") return "product-list";
    return ROUTE_SEO[routePath] || null;
};

export default PAGE_SEO;
